// existing customer's requests
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  TrendingUp as AnalyticsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import RequestModal from './RequestModal';

const CustomerRequest = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');
  const [requestDetailsOpen, setRequestDetailsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  // Mock data for customer requests
  const mockRequests = [
    {
      id: 1,
      customerName: 'TechCorp Solutions',
      customerEmail: 'john@techcorp.com',
      requestType: 'add_user',
      requestDetails: 'Add 5 new users to Basic plan',
      status: 'pending',
      amount: 149.95,
      requestDate: '2024-01-15',
      priority: 'medium',
      currentUsers: 15,
      requestedUsers: 20,
      currentCredits: 1500,
      requestedCredits: 1500,
      currentPlanType: 'basic' // Valid request for basic plan
    },
    {
      id: 2,
      customerName: 'Global Industries',
      customerEmail: 'jane@global.com',
      requestType: 'add_credits',
      requestDetails: 'Add 1000 additional credits',
      status: 'payment_pending',
      amount: 100.00,
      requestDate: '2024-01-14',
      priority: 'high',
      currentUsers: 35,
      requestedUsers: 35,
      currentCredits: 4000,
      requestedCredits: 5000,
      currentPlanType: 'advance' // Valid request for advance plan
    },
    {
      id: 3,
      customerName: 'StartupXYZ',
      customerEmail: 'mike@startupxyz.com',
      requestType: 'upgrade',
      requestDetails: 'Upgrade from Free to Basic plan',
      status: 'approved',
      amount: 299.90,
      requestDate: '2024-01-13',
      priority: 'low',
      currentUsers: 3,
      requestedUsers: 20,
      currentCredits: 400,
      requestedCredits: 2000,
      currentPlanType: 'free' // Valid request for free plan
    },
    {
      id: 4,
      customerName: 'TechCorp Solutions',
      customerEmail: 'john@techcorp.com',
      requestType: 'renew',
      requestDetails: 'Renew Basic plan for 6 months',
      status: 'processed',
      amount: 179.94,
      requestDate: '2024-01-12',
      priority: 'medium',
      currentUsers: 20,
      requestedUsers: 20,
      currentCredits: 2000,
      requestedCredits: 2000,
      currentPlanType: 'basic' // Valid request for basic plan
    },
    {
      id: 5,
      customerName: 'Invalid Request Corp',
      customerEmail: 'invalid@example.com',
      requestType: 'add_credits',
      requestDetails: 'Add credits to Basic plan',
      status: 'pending',
      amount: 50.00,
      requestDate: '2024-01-16',
      priority: 'medium',
      currentUsers: 10,
      requestedUsers: 10,
      currentCredits: 1000,
      requestedCredits: 1500,
      currentPlanType: 'basic' // Invalid request - add_credits not allowed for basic plan
    },
    {
      id: 6,
      customerName: 'Free Plan User',
      customerEmail: 'free@example.com',
      requestType: 'add_user',
      requestDetails: 'Add users to Free plan',
      status: 'pending',
      amount: 25.00,
      requestDate: '2024-01-17',
      priority: 'low',
      currentUsers: 2,
      requestedUsers: 5,
      currentCredits: 200,
      requestedCredits: 200,
      currentPlanType: 'free' // Invalid request - add_user not allowed for free plan
    }
  ];

  useEffect(() => {
    setRequests(mockRequests);
  }, []);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleRequestTypeFilter = (event) => {
    setRequestTypeFilter(event.target.value);
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setRequestDetailsOpen(true);
  };

  const handleRequestAction = (requestId, action) => {
    setSnackbar({
      open: true,
      message: `Request ${action} successfully`,
      severity: 'success'
    });
    setRequestDetailsOpen(false);
  };

  const handleProcessRequest = (requestId) => {
    navigate(`/customer-subscriptions/process/${requestId}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      payment_pending: 'info',
      processed: 'primary'
    };
    return colors[status] || 'default';
  };

  const getRequestTypeColor = (type) => {
    const colors = {
      add_user: 'primary',
      add_credits: 'secondary',
      upgrade: 'success',
      renew: 'info'
    };
    return colors[type] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'error',
      medium: 'warning',
      low: 'success'
    };
    return colors[priority] || 'default';
  };

  // Plan-based request validation
  const getValidRequestTypes = (currentPlan) => {
    const validRequests = {
      free: ['upgrade'],
      basic: ['upgrade', 'renew', 'add_user'],
      advance: ['renew', 'add_user', 'add_credits']
    };
    return validRequests[currentPlan] || [];
  };

  const isRequestValid = (request) => {
    const validTypes = getValidRequestTypes(request.currentPlanType);
    return validTypes.includes(request.requestType);
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesType = requestTypeFilter === 'all' || request.requestType === requestTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Customer Subscription Requests
        </Typography>
        <Button
          variant="contained"
          startIcon={<AnalyticsIcon />}
          onClick={() => navigate('/customer-subscriptions/analytics')}
        >
          View Analytics
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by customer name or email..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusFilter}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="payment_pending">Payment Pending</MenuItem>
                  <MenuItem value="processed">Processed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Request Type</InputLabel>
                <Select
                  value={requestTypeFilter}
                  label="Request Type"
                  onChange={handleRequestTypeFilter}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="add_user">Add User</MenuItem>
                  <MenuItem value="add_credits">Add Credits</MenuItem>
                  <MenuItem value="upgrade">Upgrade</MenuItem>
                  <MenuItem value="renew">Renew</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {/* Handle refresh */}}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {/* Handle advanced filters */}}
                >
                  Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Current Plan</TableCell>
                  <TableCell>Request Type</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Request Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests.map((request) => {
                  const isValid = isRequestValid(request);
                  return (
                    <TableRow key={request.id} hover sx={{ 
                      bgcolor: !isValid ? 'warning.light' : 'inherit',
                      '&:hover': {
                        bgcolor: !isValid ? 'warning.main' : 'action.hover'
                      }
                    }}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {request.customerName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {request.customerEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.currentPlanType.toUpperCase()}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={request.requestType.replace('_', ' ').toUpperCase()}
                            color={isValid ? getRequestTypeColor(request.requestType) : 'error'}
                            size="small"
                          />
                          {!isValid && (
                            <Chip
                              label="INVALID"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {request.requestDetails}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        ${request.amount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.priority.toUpperCase()}
                        color={getPriorityColor(request.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{request.requestDate}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewRequest(request)}
                          color="primary"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <RequestModal
        open={requestDetailsOpen}
        onClose={() => setRequestDetailsOpen(false)}
        request={selectedRequest}
        onAction={handleRequestAction}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerRequest;